import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { CalendarDays, Download } from 'lucide-react';
import { SiGoogle } from 'react-icons/si';
import type { IconBaseProps } from 'react-icons';
import { Appointment } from '../services/patientApi';
import { RootState } from '../../redux/store';
import { stripHTML, format12HourTo24Hour } from '../../utils/helpers';
import { EXTERNAL_URLS } from '../constants';
import { useClickOutside } from '../hooks/useClickOutside';
import { useOnEscape } from '../hooks/useOnEscape';

// react-icons returns ReactNode, which React 19's JSX types reject.
// Re-cast to a plain FC so we can use it as a JSX element.
const GoogleIcon = SiGoogle as unknown as React.FC<IconBaseProps>;

// ─── helpers ──────────────────────────────────────────────────────────────────
const eventTitle = (appt: Appointment, practiceName: string) => {
  const base = practiceName ? `Appointment at ${practiceName}` : 'Appointment';
  return appt.provider.name ? `${base} with ${appt.provider.name}` : base;
};

// "10:00 AM" -> "100000" (HHMMSS, 24h)
const timeToHHMMSS = (t: string): string => {
  const time24 = format12HourTo24Hour(t);
  if (!time24) return '000000';
  return `${time24.replace(':', '')}00`;
};

const buildEventTimes = (appt: Appointment) => {
  const dateStr = appt.date.replace(/-/g, '');
  const start = timeToHHMMSS(appt.startTime);
  const end = timeToHHMMSS(appt.endTime);
  return {
    googleStart: `${dateStr}T${start}`,
    googleEnd: `${dateStr}T${end}`,
    isoStart: `${appt.date}T${start.slice(0, 2)}:${start.slice(2, 4)}:00`,
    isoEnd: `${appt.date}T${end.slice(0, 2)}:${end.slice(2, 4)}:00`,
  };
};

const buildGoogleUrl = (appt: Appointment, practiceName: string, location?: string): string => {
  const { googleStart, googleEnd } = buildEventTimes(appt);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: eventTitle(appt, practiceName),
    dates: `${googleStart}/${googleEnd}`,
    location: location ?? '',
  });
  return `${EXTERNAL_URLS.GOOGLE_CALENDAR_RENDER}?${params.toString()}`;
};

// DTSTAMP must be the *moment the file was generated* in UTC.
// Format: YYYYMMDDTHHMMSSZ — derived from ISOString stripped of separators.
const nowAsDtstamp = (): string =>
  new Date().toISOString().replace(/[-:.]/g, '').slice(0, 15) + 'Z';

const buildICSContent = (appt: Appointment, practiceName: string, location?: string): string => {
  const { googleStart, googleEnd } = buildEventTimes(appt);
  const escape = (s: string) => s.replace(/[\\,;]/g, '\\$&').replace(/\n/g, '\\n');
  // Patient's local timezone — best available since portal data doesn't carry the practice's tz.
  const tzid = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//TeamCare//Patient Portal//EN',
    'BEGIN:VEVENT',
    `UID:appointment-${appt.id}@teamcare`,
    `DTSTAMP:${nowAsDtstamp()}`,
    `DTSTART;TZID=${tzid}:${googleStart}`,
    `DTEND;TZID=${tzid}:${googleEnd}`,
    `SUMMARY:${escape(eventTitle(appt, practiceName))}`,
    `LOCATION:${escape(location ?? '')}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
};

const downloadICS = (appt: Appointment, practiceName: string, location?: string): void => {
  const blob = new Blob([buildICSContent(appt, practiceName, location)], {
    type: 'text/calendar;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `appointment-${appt.id}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ─── component ────────────────────────────────────────────────────────────────
interface AddToCalendarProps {
  appointment: Appointment;
  /** Visual preset for the trigger. "default" suits white surfaces; "ghost" suits dark backgrounds. */
  variant?: 'default' | 'ghost';
  /**
   * Classes for the trigger button.
   *  - By default these are appended to the variant preset.
   *  - Pass `replaceTrigger` to drop the preset entirely and fully restyle.
   */
  triggerClassName?: string;
  /** When true, `triggerClassName` replaces the variant preset instead of extending it. */
  replaceTrigger?: boolean;
  /** Custom label, defaults to "Add to Calendar". */
  label?: string;
  /** Override the trigger icon size (px). */
  iconSize?: number;
  /** Hide the trigger icon entirely. */
  hideIcon?: boolean;
  /** Classes appended to the dropdown panel. */
  menuClassName?: string;
  /** Classes appended to every menu item (link + buttons). */
  itemClassName?: string;
}

const MENU_WIDTH = 208; // matches w-52
const MENU_GAP = 8; // visual gap between trigger and menu
const VIEWPORT_PADDING = 8; // keep menu this far from viewport edges
const ESTIMATED_MENU_HEIGHT = 152; // ~3 rows × 44px + paddings; only used to decide flip-up

const TRIGGER_PRESETS = {
  default:
    'inline-flex items-center justify-center gap-2 text-sm font-medium text-secondary-700 bg-white border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50 transition-colors',
  ghost:
    'inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-white/15 hover:bg-white/25 rounded-md px-2.5 py-1 transition-colors',
};

/**
 * Reusable "Add to Calendar" dropdown.
 * Renders the menu in a portal on document.body so it escapes any
 * `overflow:hidden` ancestor (e.g. cards) and never gets clipped.
 */
export const AddToCalendar = ({
  appointment,
  variant = 'default',
  triggerClassName = '',
  replaceTrigger = false,
  label = 'Add to Calendar',
  iconSize,
  hideIcon = false,
  menuClassName = '',
  itemClassName = '',
}: AddToCalendarProps) => {
  const practiceInfo = useSelector((s: RootState) => s.patientAuth.practiceInfo);
  const practiceName = practiceInfo?.name ?? '';
  const location = practiceInfo?.address ? stripHTML(practiceInfo.address) : undefined;

  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const updatePosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Left-align with the trigger by default. If the menu would overflow the
    // right viewport edge, fall back to right-aligning with the trigger.
    let left = rect.left;
    if (left + MENU_WIDTH > vw - VIEWPORT_PADDING) {
      left = rect.right - MENU_WIDTH;
    }
    // Final clamp so the menu never escapes the viewport.
    left = Math.max(VIEWPORT_PADDING, Math.min(left, vw - MENU_WIDTH - VIEWPORT_PADDING));

    // Open downward unless there isn't room — then flip above.
    const spaceBelow = vh - rect.bottom;
    const top =
      spaceBelow < ESTIMATED_MENU_HEIGHT + MENU_GAP && rect.top > ESTIMATED_MENU_HEIGHT + MENU_GAP
        ? rect.top - ESTIMATED_MENU_HEIGHT - MENU_GAP
        : rect.bottom + MENU_GAP;

    setPos({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open]);

  // Close handlers: outside click + Escape (shared portal hooks).
  const close = () => setOpen(false);
  useClickOutside([triggerRef, menuRef], close, open);
  useOnEscape(close, open);

  // While open, keep the portal-rendered menu pinned to the trigger as the
  // page scrolls or resizes underneath.
  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  const triggerClasses = replaceTrigger
    ? triggerClassName
    : `${TRIGGER_PRESETS[variant]} ${triggerClassName}`.trim();
  const resolvedIconSize = iconSize ?? (variant === 'ghost' ? 12 : 15);
  const itemBase = 'flex items-center gap-3 px-3 py-2.5 text-sm text-secondary-700 hover:bg-gray-50';
  const itemClasses = `${itemBase} ${itemClassName}`.trim();

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={triggerClasses}
      >
        {!hideIcon && <CalendarDays size={resolvedIconSize} />}
        {label}
      </button>

      {open && pos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: MENU_WIDTH }}
          className={`bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden animate-scale-in origin-top-right ${menuClassName}`.trim()}
        >
          <a
            role="menuitem"
            href={buildGoogleUrl(appointment, practiceName, location)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className={itemClasses}
          >
            <GoogleIcon size={15} color="#4285F4" />
            Google Calendar
          </a>
          <button
            role="menuitem"
            type="button"
            onClick={() => {
              downloadICS(appointment, practiceName, location);
              setOpen(false);
            }}
            className={`w-full ${itemClasses} border-t border-gray-100`}
          >
            <Download size={15} className="text-secondary-500" />
            Download .ics file
          </button>
        </div>,
        document.body,
      )}
    </>
  );
};
